import { useEffect, useMemo, useState } from 'react';
import type { DensitySnapshot } from '@vigilo/shared';
import { get } from '@vigilo/api-client';
import { CROWD_SIMULATION_RESET_EVENT } from './useSimulation';

export interface CurrentZoneMetrics {
  operationId: string;
  zoneId: string;
  venueId: string | null;
  occupancyCount: number;
  densityScore: number;
  capacityUtilisation: number;
  trendDirection: DensitySnapshot['trendDirection'];
  trendRate: number;
  entryRate: number | null;
  exitRate: number | null;
  netFlow: number | null;
  congestionScore: number | null;
  congestionActive: boolean | null;
  dominantDirection: number | null;
  compositionTotal: number | null;
  newEntrants: number | null;
  repeatVisitors: number | null;
  dwellOverThreshold: number | null;
  status: 'normal' | 'warning' | 'alert';
  lastEventAt: string;
  updatedAt: string;
  freshnessStatus?: 'fresh' | 'stale';
  freshnessAgeMs?: number;
  freshnessWindowMs?: number;
  freshnessEvaluatedAt?: string;
}

export interface DensityHistoryPoint {
  operationId: string | null;
  zoneId: string;
  occupancyCount: number;
  densityScore: number;
  capacityUtilisation: number;
  sampledAt: string;
  redacted: true;
  governanceProfile: 'VIGILO_CROWD';
  source: 'crowd_density_history_projection';
}

export interface FlowHistoryPoint {
  operationId: string | null;
  zoneId: string;
  entryRate: number | null;
  exitRate: number | null;
  netFlow: number | null;
  congestionScore: number | null;
  congestionActive: boolean | null;
  dominantDirection: number | null;
  sampledAt: string;
  redacted: true;
  governanceProfile: 'VIGILO_CROWD';
  source: 'crowd_flow_vector_projection';
}

export const ZONE_METRICS_FRESHNESS_WINDOW_MS = 15_000;

interface ZoneMetricsState {
  rows: CurrentZoneMetrics[];
  byZone: Record<string, CurrentZoneMetrics>;
  loading: boolean;
  error: string | null;
}

interface DensityHistoryState {
  rows: DensityHistoryPoint[];
  byZone: Record<string, DensityHistoryPoint[]>;
  loading: boolean;
  error: string | null;
}

interface FlowHistoryState {
  rows: FlowHistoryPoint[];
  byZone: Record<string, FlowHistoryPoint[]>;
  loading: boolean;
  error: string | null;
}

function unwrapMetrics(body: CurrentZoneMetrics[] | { data?: CurrentZoneMetrics[] }): CurrentZoneMetrics[] {
  return Array.isArray(body) ? body : (body.data ?? []);
}

function unwrapDensityHistory(body: DensityHistoryPoint[] | { data?: DensityHistoryPoint[] }): DensityHistoryPoint[] {
  return Array.isArray(body) ? body : (body.data ?? []);
}

function unwrapFlowHistory(body: FlowHistoryPoint[] | { data?: FlowHistoryPoint[] }): FlowHistoryPoint[] {
  return Array.isArray(body) ? body : (body.data ?? []);
}

export type DensitySnapshotWithFlow = DensitySnapshot &
  Pick<CurrentZoneMetrics, 'dominantDirection' | 'entryRate' | 'exitRate' | 'netFlow'>;

export function zoneMetricsDensitySnapshots(rows: CurrentZoneMetrics[]): Record<string, DensitySnapshotWithFlow> {
  return Object.fromEntries(rows.map(row => [
    row.zoneId,
    {
      zoneId: row.zoneId,
      occupancyCount: row.occupancyCount,
      densityScore: row.densityScore,
      capacityUtilisation: row.capacityUtilisation,
      trendDirection: row.trendDirection,
      trendRate: row.trendRate,
      timestamp: row.lastEventAt,
      dominantDirection: row.dominantDirection,
      entryRate: row.entryRate,
      exitRate: row.exitRate,
      netFlow: row.netFlow,
    },
  ]));
}

export function isZoneMetricFresh(
  row: Pick<CurrentZoneMetrics, 'lastEventAt' | 'updatedAt' | 'freshnessStatus' | 'freshnessWindowMs'> | null | undefined,
  nowMs = Date.now(),
  freshnessWindowMs = ZONE_METRICS_FRESHNESS_WINDOW_MS,
): boolean {
  if (!row) return false;
  if (row.freshnessStatus) return row.freshnessStatus === 'fresh';
  const eventMs = Date.parse(row.lastEventAt || row.updatedAt);
  const effectiveFreshnessWindowMs = row.freshnessWindowMs ?? freshnessWindowMs;
  return Number.isFinite(eventMs) && nowMs - eventMs <= effectiveFreshnessWindowMs;
}

export function useLiveZoneMetrics(operationId?: string | null, enabled = true, pollMs = 2000): ZoneMetricsState {
  const [rows, setRows] = useState<CurrentZoneMetrics[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let timer: number | undefined;
    let inFlight = false;
    const reset = () => setRows([]);
    window.addEventListener(CROWD_SIMULATION_RESET_EVENT, reset);

    async function load() {
      if (inFlight) return;
      inFlight = true;
      setLoading(true);
      try {
        const qs = operationId ? `?operationId=${encodeURIComponent(operationId)}` : '';
        const body = await get<CurrentZoneMetrics[] | { data?: CurrentZoneMetrics[] }>(`/live-zone-metrics/current${qs}`);
        if (!cancelled) {
          setRows(unwrapMetrics(body));
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load metrics');
      } finally {
        if (!cancelled) setLoading(false);
        inFlight = false;
      }
    }

    function scheduleNext() {
      if (cancelled) return;
      timer = window.setTimeout(() => {
        void load().finally(scheduleNext);
      }, pollMs);
    }

    void load().finally(scheduleNext);
    return () => {
      cancelled = true;
      window.removeEventListener(CROWD_SIMULATION_RESET_EVENT, reset);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [enabled, operationId, pollMs]);

  const byZone = useMemo(
    () => Object.fromEntries(rows.map(row => [row.zoneId, row])),
    [rows],
  );

  return { rows, byZone, loading, error };
}

export function useDensityHistory(
  operationId: string | null | undefined,
  fromIso: string | null | undefined,
  toIso: string | null | undefined,
  enabled = true,
): DensityHistoryState {
  const [rows, setRows] = useState<DensityHistoryPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !operationId || !fromIso || !toIso) {
      setRows([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    const scopedOperationId = operationId;
    const scopedFrom = fromIso;
    const scopedTo = toIso;
    async function load() {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          operationId: scopedOperationId,
          from: scopedFrom,
          to: scopedTo,
        });
        const body = await get<DensityHistoryPoint[] | { data?: DensityHistoryPoint[] }>(
          `/live-zone-metrics/density-history?${params.toString()}`,
        );
        if (!cancelled) {
          setRows(unwrapDensityHistory(body));
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load density history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [enabled, fromIso, operationId, toIso]);

  const byZone = useMemo(() => {
    const grouped: Record<string, DensityHistoryPoint[]> = {};
    for (const row of rows) {
      grouped[row.zoneId] = [...(grouped[row.zoneId] ?? []), row];
    }
    return grouped;
  }, [rows]);

  return { rows, byZone, loading, error };
}

export function useFlowHistory(
  operationId: string | null | undefined,
  fromIso: string | null | undefined,
  toIso: string | null | undefined,
  enabled = true,
): FlowHistoryState {
  const [rows, setRows] = useState<FlowHistoryPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !operationId || !fromIso || !toIso) {
      setRows([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    const scopedOperationId = operationId;
    const scopedFrom = fromIso;
    const scopedTo = toIso;
    async function load() {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          operationId: scopedOperationId,
          from: scopedFrom,
          to: scopedTo,
        });
        const body = await get<FlowHistoryPoint[] | { data?: FlowHistoryPoint[] }>(
          `/live-zone-metrics/flow-history?${params.toString()}`,
        );
        if (!cancelled) {
          setRows(unwrapFlowHistory(body));
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load flow history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [enabled, fromIso, operationId, toIso]);

  const byZone = useMemo(() => {
    const grouped: Record<string, FlowHistoryPoint[]> = {};
    for (const row of rows) {
      grouped[row.zoneId] = [...(grouped[row.zoneId] ?? []), row];
    }
    return grouped;
  }, [rows]);

  return { rows, byZone, loading, error };
}
